import { AlertTriangle, CheckCircle2, CircleX, DatabaseZap, RefreshCw, ShieldAlert, X } from 'lucide-react'
import type { BrowserCompatibilityReport, CompatibilityItem } from '../browser-compatibility'

const itemIcon = (item: CompatibilityItem) => {
  if (item.status === 'supported') return <CheckCircle2 size={18} />
  if (item.status === 'missing') return <CircleX size={18} />
  return <AlertTriangle size={18} />
}

interface CompatibilityDialogProps {
  report: BrowserCompatibilityReport
  onClose: () => void
}

export function CompatibilityDialog({ report, onClose }: CompatibilityDialogProps) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal compatibility-dialog" role="dialog" aria-modal="true" aria-labelledby="compatibility-title">
        <div className="compatibility-heading">
          <div><span className="modal-kicker">Feature detection</span><h2 id="compatibility-title">Browser compatibility</h2></div>
          <button className="icon-button" aria-label="Close browser compatibility" onClick={onClose}><X size={17} /></button>
        </div>
        <p>Deckelier checks actual browser capabilities instead of guessing from the browser name. Limited features are listed clearly below.</p>
        <div className="compatibility-list">
          {report.items.map((item) => <article className={item.status} key={item.id}>{itemIcon(item)}<div><strong>{item.label}</strong><span>{item.detail}</span></div><small>{item.status}</small></article>)}
        </div>
        <div className="modal-actions"><button className="button primary" onClick={onClose}>Done</button></div>
      </section>
    </div>
  )
}

export function BrowserCompatibilityScreen({ report }: { report: BrowserCompatibilityReport }) {
  return (
    <main className="compatibility-screen">
      <div className="compatibility-screen-icon"><ShieldAlert size={31} /></div>
      <span className="modal-kicker">Browser not supported</span>
      <h1>Deckelier cannot run safely here</h1>
      <p>The browser is missing {report.blockers.length === 1 ? 'a required capability' : 'required capabilities'}. Update it or open Deckelier in a current version of Chrome, Edge, Firefox, or Safari.</p>
      <div className="compatibility-list blockers">
        {report.blockers.map((item) => <article className="missing" key={item.id}>{itemIcon(item)}<div><strong>{item.label}</strong><span>{item.detail}</span></div></article>)}
      </div>
      <button className="button primary" onClick={() => window.location.reload()}><RefreshCw size={16} /> Check again</button>
    </main>
  )
}

export function BrowserStorageFailureScreen({ error }: { error: Error }) {
  return (
    <main className="compatibility-screen">
      <div className="compatibility-screen-icon"><DatabaseZap size={31} /></div>
      <span className="modal-kicker">Local storage unavailable</span>
      <h1>Deckelier cannot open its project library</h1>
      <p>This usually means browser storage is disabled, blocked by private browsing policy, or temporarily unavailable. Your existing browser data was not deleted.</p>
      <details className="crash-details"><summary>Technical details</summary><pre>{error.message}</pre></details>
      <button className="button primary compatibility-retry" onClick={() => window.location.reload()}><RefreshCw size={16} /> Try again</button>
    </main>
  )
}
